import { useState } from "react";
import { Archive, Copy, Crown, Eraser, Send, Trash2, User } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { copyText, transferTo } from "../../lib/publisherTools";

const LABELS = ["ad", "premium", "normal"];
const isLabel = (s) => LABELS.includes(String(s || "").trim().toLowerCase());

export function extractUsernames(raw) {
  const lines = String(raw || "").split("\n");
  const premium = [], normal = [];
  const seen = new Set();
  const usedLabel = new Set();
  const push = (cand, cat) => {
    cand = String(cand || "").replace(/^@+/, "");
    if (!cand || /\s/.test(cand) || isLabel(cand)) return;
    const key = cand.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    (cat === "premium" ? premium : normal).push(cand);
  };
  for (let i = 0; i < lines.length; i++) {
    const low = lines[i].trim().toLowerCase();
    if (low === "ad") {
      let cand = null;
      for (let j = i - 1; j >= 0; j--) {
        const c = lines[j].trim();
        if (!c) continue;
        cand = c;
        break;
      }
      let cat = "normal";
      for (let k = i + 1; k < Math.min(i + 4, lines.length); k++) {
        const l2 = lines[k].trim().toLowerCase();
        if (!l2) continue;
        if (l2 === "premium") { cat = "premium"; usedLabel.add(k); break; }
        if (l2 === "normal") { usedLabel.add(k); break; }
        if (l2 === "ad") break;
      }
      if (cand) push(cand, cat);
    } else if ((low === "premium" || low === "normal") && !usedLabel.has(i)) {
      usedLabel.add(i);
      let cand = null;
      for (let j = i - 1; j >= 0; j--) {
        const c = lines[j].trim();
        if (!c || c.toLowerCase() === "ad") continue;
        cand = c;
        break;
      }
      if (cand) push(cand, low);
    }
  }
  return { premium, normal };
}

export default function TextCleaner() {
  const [raw, setRaw] = useState("");
  const [premium, setPremium] = useState([]);
  const [normal, setNormal] = useState([]);
  const navigate = useNavigate();

  const total = premium.length + normal.length;

  const clean = (andCopy = false) => {
    const r = extractUsernames(raw);
    setPremium(r.premium);
    setNormal(r.normal);
    const n = r.premium.length + r.normal.length;
    if (!n) { toast.error("Kullanıcı adı bulunamadı"); return; }
    toast.success(`${n} kullanıcı adı çıkarıldı (${r.premium.length} premium, ${r.normal.length} normal)`);
    if (andCopy) copyText([...r.premium, ...r.normal].join("\n")).then(() => toast.success(`${n} kullanıcı adı kopyalandı.`));
  };

  const copyGroup = (list, label) => {
    if (!list.length) { toast.error(`${label} listesi boş`); return; }
    copyText(list.join("\n")).then(() => toast.success(`${list.length} ${label} kullanıcı adı kopyalandı.`));
  };

  const combined = () => [...premium, ...normal];

  const sendTo = (target) => {
    if (!total) { toast.error("Önce metni temizleyin"); return; }
    transferTo(combined());
    toast.success(`${total} kullanıcı aktarıldı`);
    navigate(target);
  };

  const archiveResult = async () => {
    if (!total) { toast.error("Önce metni temizleyin"); return; }
    try {
      const r = await api.post("/admin/archive/add", { usernames: combined() });
      toast.success(r.data.message);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const inputCls = "w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-[#64748B] outline-none focus:border-cyan-400/60 transition-colors font-mono text-xs resize-y";
  const cardCls = "rounded-2xl border border-white/[0.07] bg-[#0A0E17] p-5";
  const btnCls = "inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-3.5 py-1.5 border transition-colors";

  return (
    <div data-testid="cleaner-page" className="space-y-6">
      <h1 className="font-display font-700 text-lg text-white flex items-center gap-2"><Eraser size={18} className="text-cyan-300" /> Metin Temizleme</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className={cardCls}>
          <h4 className="text-[11px] font-bold text-cyan-300 mb-3">HAM METİN</h4>
          <textarea value={raw} onChange={(e) => setRaw(e.target.value)} rows={16} placeholder={"fp.luapm\nAd\n千ヤ・LUA 𓆩❤︎𓆪\nPremium\nshirazinmyntrls911\nAd\n👑SUNRİSE👑\nNormal"} data-testid="cleaner-input" className={inputCls} />
        </div>
        <div className={cardCls} data-testid="cleaner-premium-card">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-[11px] font-bold text-amber-300 flex items-center gap-1.5"><Crown size={13} /> PREMIUM <span className="text-white">({premium.length})</span></h4>
            <button onClick={() => copyGroup(premium, "premium")} data-testid="cleaner-copy-premium" className={`${btnCls} text-amber-300 border-amber-400/30 hover:bg-amber-400/10`}><Copy size={12} /> Kopyala</button>
          </div>
          <textarea value={premium.join("\n")} readOnly rows={14} placeholder="Premium kullanıcılar burada görünecek..." data-testid="cleaner-output-premium" className={`${inputCls} bg-black/20 focus:border-amber-400/60`} />
        </div>
        <div className={cardCls} data-testid="cleaner-normal-card">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-[11px] font-bold text-cyan-300 flex items-center gap-1.5"><User size={13} /> NORMAL <span className="text-white">({normal.length})</span></h4>
            <button onClick={() => copyGroup(normal, "normal")} data-testid="cleaner-copy-normal" className={`${btnCls} text-cyan-300 border-cyan-400/30 hover:bg-cyan-400/10`}><Copy size={12} /> Kopyala</button>
          </div>
          <textarea value={normal.join("\n")} readOnly rows={14} placeholder="Normal kullanıcılar burada görünecek..." data-testid="cleaner-output-normal" className={`${inputCls} bg-black/20`} />
        </div>
      </div>

      <div className={`${cardCls} flex flex-wrap gap-2`} data-testid="cleaner-actions">
        <button onClick={() => clean(false)} data-testid="cleaner-clean" className="btn-glow inline-flex items-center gap-2 text-xs font-semibold text-white px-5 py-2.5 rounded-full"><Eraser size={13} /> Temizle</button>
        <button onClick={() => clean(true)} data-testid="cleaner-clean-copy" className={`${btnCls} text-cyan-300 border-cyan-400/30 hover:bg-cyan-400/10`}><Eraser size={12} /> Temizle ve Kopyala</button>
        <button onClick={() => { setRaw(""); setPremium([]); setNormal([]); }} data-testid="cleaner-clear" className={`${btnCls} text-red-300 border-red-400/30 hover:bg-red-500/10`}><Trash2 size={12} /> Listeyi Sil</button>
        <span className="flex-1" />
        <button onClick={archiveResult} data-testid="cleaner-to-archive" className={`${btnCls} text-[#A0ABC0] border-white/10 hover:text-white`}><Archive size={12} /> Arşive Gönder</button>
        <button onClick={() => sendTo("/admin/mesaj")} data-testid="cleaner-to-message" className={`${btnCls} text-[#A0ABC0] border-white/10 hover:text-white`}><Send size={12} /> Toplu Mesaja Gönder</button>
      </div>

      <p className="text-[11px] text-[#64748B]" data-testid="cleaner-rule-note">Kural: "Ad" satırının üstündeki kullanıcı adı alınır; görünen ismin altındaki satır "Premium" ise premium, "Normal" ise normal listesine girer. Baştaki @ temizlenir, tekrarlar tekle indirilir.</p>
    </div>
  );
}
